<template>
  <section class="filters" data-cy="product-filters-menu">
    <div class="filters__actions">
      <input
        type="button"
        value="Clear"
        class="filter-button-clear"
        @click.prevent="clearFilters"
        data-cy="clear-filters-button"
      />
      <input
        type="button"
        value="Apply"
        class="filter-button-apply"
        @click.prevent="applyFilters"
        data-cy="apply-filters-button"
      />
    </div>
    <ul class="filters__list">
      <li v-for="item in filters" :key="item.label" data-cy="filters-item">
        <label data-cy="filters-item-label">{{ item.label }}</label>
        <DropDownList
          class="filters-dropdown"
          :data-items="item.values"
          v-model="item.value"
          data-cy="filters-item-dropdown"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { EnumDpm } from "@/Domain/EnumDpm";
import { onBeforeMount, ref, watch } from "vue";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { convertBackToNumber } from "@/Domain/EnumGroupConverter";
import { getLabelTypeFromString } from "@/shared/helpers/enumHelper";

import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import type { ProductWithSelection } from "@/api/kosherCertificates";
import { boolYNConvertBack } from "@/shared/helpers/filtersHelper";

const props = defineProps<{
  filters: FilterMenuItem[];
  getPropValues: (field: string) => Promise<ProductWithSelection[]>;
}>();

const emits = defineEmits<{
  (event: "apply", filters: FilterMenuItem[]): void;
  (event: "clear"): void;
  (event: "close"): void;
}>();

let dpmValues = Object.values(EnumDpm);
dpmValues = dpmValues.slice(0, dpmValues.length / 2);
const passoverValues = ["Y", "N", "Both"];
// const ciValues = ["Consumer", "Industrial", "Both"];
const conditions = ["Y", "N", "Both"];

const filters = ref([
    {
    field: "PlantName",
    label: "Plant Name",
    placeholder: "Select a Plant",
    values: [] as string[],
    value: "Select a Plant",
  },
  {
    field: "BrandName",
    label: "Brand",
    placeholder: "Select a Brand",
    values: [] as string[],
    value: "Select a Brand",
  },
  {
    field: "_Dpm",
    label: "DPM",
    placeholder: "Select a DPM",
    values: [] as string[],
    value: "Select a DPM",
    getFilterValue: (value: string) => {
      return EnumDpm[value as keyof typeof EnumDpm];
    },
  },
  {
    field: "_Group",
    label: "Group",
    placeholder: "Select a Group",
    values: [] as string[],
    value: "Select a Group",
    getFilterValue: (value: string) => {
      return convertBackToNumber(value);
    },
  },
  {
    field: "_Status",
    label: "Product Status",
    placeholder: "Select a Product Status",
    values: [] as string[],
    value: "Select a Product Status",
    getFilterValue: (value: string) => {
      return EnumMerchStatus[value as keyof typeof EnumMerchStatus];
    },
  },
  {
    field: "_LabelType",
    label: "Label Type",
    placeholder: "Select a Label Type",
    values: [] as string[],
    value: "Select a Label Type",
    getFilterValue: (value: string) => {
      return getLabelTypeFromString(value);
    },
  },
  {
    field: "SpecialRequirementsExists",
    label: "Special Requirements",
    placeholder: "Select Special Requirements",
    values: conditions,
    value: "Select Special Requirements",
    getFilterValue: boolYNConvertBack,
  },
  {
    field: "_Pesach",
    label: "Passover",
    placeholder: "Select a Passover Condition",
    values: passoverValues,
    value: "Select a Passover Condition",
    getFilterValue: boolYNConvertBack,
  },
   // {
  //   field: "Symbol",
  //   label: "Symbol",
  //   placeholder: "Select a Symbol",
  //   values: [] as string[],
  //   value: "Select a Symbol",
  // },
  // {
  //   field: "_ConsumerIndustrial",
  //   label: "Consumer/Industrial",
  //   placeholder: "Select Consumer/Industrial",
  //   values: ciValues,
  //   value: "Select Consumer/Industrial",
  //   getFilterValue: (value: string) => {
  //     return value === "Consumer"
  //       ? "C"
  //       : value === "Industrial"
  //         ? "I"
  //         : "CI";
  //   },
  // },
] as FilterMenuItem[]);

const setFilterValues = () => {
  props.filters.forEach((f) => {
    const filter = filters.value.find((item) => item.field === f.field);
    if (filter) {
      filter.value = f.value;
    }
  });
};

setFilterValues();

const applyFilters = () => {
  const setFilters = filters.value.filter((f) => f.value !== f.placeholder);

  emits("apply", setFilters);
  emits("close");
};

const clearFilters = () => {
  emits("apply", []);
  emits("close");
};

onBeforeMount(async () => {
  for (const filter of filters.value.filter((f) => f.values.length === 0)) {
    const values = await props.getPropValues(filter.field);
    filter.values = values.map((item) => (item as any)[filter.field]);
  }
});

watch(
  () => props.filters,
  () => {
    setFilterValues();
  }
);
</script>

<style lang="scss">
.filters {
  position: absolute;
  background: #fff;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.3);
  width: 100%;
  padding: 20px;
  overflow: auto;
  display: block;
  z-index: 100;
  height: 500px;

  &__actions {
    padding: 15px;
    border-bottom: 1px solid #ddd;
    margin: -20px -20px 10px;
    background: #efefef;
    justify-content: flex-end;
    display: flex;

    .filter-button-clear {
      text-align: center;
      padding: 8px 20px;
      box-shadow: none;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      background: 0 0;
      color: #3a5da6 !important;
      display: block;
      margin: 0 5px;
    }

    .filter-button-apply {
      text-align: center;
      color: #fff !important;
      padding: 8px 20px;
      box-shadow: none;
      background: #3a5da6;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      display: block;
      margin: 0 5px;
    }
  }

  &__list {
    padding-left: 0;

    li {
      list-style-type: none;
    }

    label {
      font-weight: 500;
      font-size: 11px;
      text-transform: uppercase;
      color: #999;
      margin-bottom: 5px;
      display: inline-block;
    }

    .filters-dropdown {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}
</style>
